import { useState } from 'react';
import Swal from 'sweetalert2';
import { Button, Input, Switch } from '@/components/ui';
import { CTA, GHOST } from '@components/ui/designTokens';
import type { JwtProcessType } from '@features/certificates/types/certificate.types';
import type { NewCompanyStep3Data } from '../../types/newCompanyWizard.types';

interface Step3CertificatesProps {
  data: NewCompanyStep3Data;
  isSubmitting: boolean;
  onFinish: (data: NewCompanyStep3Data) => void;
  onBack: () => void;
}

function CertificateDropzone({
  id,
  label,
  file,
  onChange,
  testId,
}: {
  id: string;
  label: string;
  file: File | null;
  onChange: (file: File | null) => void;
  testId?: string;
}) {
  return (
    <div>
      <div className="field-label-dark">
        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
          {label}
        </label>
      </div>
      <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-2">
        Coloque el certificado en formato <strong>.PFX o .P12</strong>
      </p>
      <div className="border-2 border-dashed border-secondary-300 dark:border-secondary-600 rounded-lg p-6">
        <input
          data-testid={testId}
          type="file"
          accept=".pfx,.p12"
          onChange={(e) => onChange(e.target.files?.[0] || null)}
          className="hidden"
          id={id}
        />
        <label htmlFor={id} className="flex flex-col items-center cursor-pointer text-center">
          <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
            Suelta el archivo aquí o haz clic para subirlo
          </span>
          {file && (
            <span className="mt-2 text-xs text-primary-600 font-mono break-all">{file.name}</span>
          )}
        </label>
      </div>
    </div>
  );
}

// Longitud mínima para un JWT/Secret válido (mismo criterio que FiscalInfoTab).
const MIN_JWT_LENGTH = 50;

export function Step3Certificates({ data, isSubmitting, onFinish, onBack }: Step3CertificatesProps) {
  const [onlySignatureCert, setOnlySignatureCert] = useState(data.onlySignatureCert);
  const [signCertFile, setSignCertFile] = useState<File | null>(data.signCertFile);
  const [authCertFile, setAuthCertFile] = useState<File | null>(data.authCertFile);
  const [certificatePassword, setCertificatePassword] = useState(data.certificatePassword);
  const [secretJwt, setSecretJwt] = useState(data.secretJwt);
  const [affiliationPac, setAffiliationPac] = useState(data.affiliationPac);
  const [jwtProcessType, setJwtProcessType] = useState<JwtProcessType>(data.jwtProcessType);
  const [jwtError, setJwtError] = useState<string | null>(null);

  const handleJwtChange = (value: string) => {
    setSecretJwt(value);
    if (value && value.length < MIN_JWT_LENGTH) {
      setJwtError(`El JWT debe tener al menos ${MIN_JWT_LENGTH} caracteres (actualmente: ${value.length}). Si no tienes el JWT, cambia la opción.`);
    } else {
      setJwtError(null);
    }
  };

  const handleFinish = () => {
    if (!signCertFile) {
      Swal.fire({
        title: 'Certificados',
        text: 'El certificado de firma es necesario. Por favor cargue el certificado.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    if (!onlySignatureCert && !authCertFile) {
      Swal.fire({
        title: 'Certificados',
        text: 'Los certificados son necesarios. Por favor cargue ambos certificados.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    if (!certificatePassword) {
      Swal.fire({
        title: 'Certificados',
        text: 'La clave del certificado es requerida.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    if (jwtProcessType === 'update' && !secretJwt) {
      Swal.fire({
        title: 'Certificados',
        text: 'Por favor ingrese el Secret JWT o cambie la opción de JWT.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    if (jwtProcessType === 'update' && secretJwt && secretJwt.length < MIN_JWT_LENGTH) {
      Swal.fire({
        title: 'Certificados',
        text: `El JWT debe tener al menos ${MIN_JWT_LENGTH} caracteres. Si no tienes el JWT, cambia la opción.`,
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    onFinish({
      onlySignatureCert,
      signCertFile,
      authCertFile: onlySignatureCert ? signCertFile : authCertFile,
      certificatePassword,
      secretJwt,
      affiliationPac,
      jwtProcessType,
    });
  };

  return (
    <div className="font-display">
      {/*
        Fila 1, en 2 columnas: a la izquierda "Solo certificado de firma" y
        "Afiliación al PAC" (opciones generales); a la derecha "Crear JWT" y
        "Actualizar JWT" juntos, con su input/aviso dependiente justo debajo.
      */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="flex flex-col gap-5">
          <div>
            <Switch
              data-testid="company-wizard-only-signature-switch"
              label="Solo certificado de firma"
              checked={onlySignatureCert}
              onChange={setOnlySignatureCert}
            />
            <p className="mt-1.5 text-xs text-secondary-500 dark:text-secondary-400">
              Si está marcado, solo necesita cargar el certificado de firma. Se usará
              automáticamente para ambos propósitos.
            </p>
          </div>

          <div>
            <Switch
              data-testid="company-wizard-affiliation-pac-switch"
              label="Afiliación al PAC"
              checked={affiliationPac}
              onChange={setAffiliationPac}
            />
            <p className="mt-1.5 text-xs text-secondary-500 dark:text-secondary-400">
              Activa esta opción para afiliar la compañía al PAC (DGI) al configurar los certificados.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Switch
            data-testid="company-wizard-create-jwt-switch"
            label="Crear JWT"
            checked={jwtProcessType === 'create'}
            onChange={(checked) => setJwtProcessType(checked ? 'create' : 'none')}
          />
          <Switch
            data-testid="company-wizard-update-jwt-switch"
            label="Actualizar JWT"
            checked={jwtProcessType === 'update'}
            onChange={(checked) => setJwtProcessType(checked ? 'update' : 'none')}
          />

          {jwtProcessType === 'update' && (
            <div>
              <Input
                data-testid="company-wizard-secret-jwt-input"
                label="Secret JWT"
                autoComplete="off"
                value={secretJwt}
                onChange={(e) => handleJwtChange(e.target.value)}
                placeholder="Ingrese el Secret JWT (mínimo 50 caracteres)"
                className={jwtError ? 'border-danger-500' : ''}
              />
              {jwtError && (
                <p data-testid="company-wizard-jwt-error" className="mt-1 text-xs text-danger-500">
                  {jwtError}
                </p>
              )}
            </div>
          )}

          {jwtProcessType !== 'update' && (
            <p className="text-xs text-secondary-500 dark:text-secondary-400">
              El JWT no es obligatorio por el momento. Solo actívalo (Crear o Actualizar) si la DGI lo requiere.
            </p>
          )}
        </div>
      </div>

      {/* Fila 2: certificados — ya era de a 2, salvo cuando "Solo certificado de firma" deja uno solo */}
      <div className={`mt-6 grid grid-cols-1 ${onlySignatureCert ? '' : 'sm:grid-cols-2'} gap-4`}>
        <CertificateDropzone
          id="new-company-sign-cert"
          label="Certificado de Firma *"
          file={signCertFile}
          onChange={setSignCertFile}
          testId="company-wizard-sign-cert-input"
        />
        {!onlySignatureCert && (
          <CertificateDropzone
            id="new-company-auth-cert"
            label="Certificado de Autenticación *"
            file={authCertFile}
            onChange={setAuthCertFile}
            testId="company-wizard-auth-cert-input"
          />
        )}
      </div>

      {/* Fila 3: clave del certificado — el Secret JWT ahora vive junto al switch de JWT, arriba */}
      <div className="field-label-dark mt-6">
        <Input
          data-testid="company-wizard-cert-password-input"
          label="Clave del certificado*"
          type="password"
          autoComplete="new-password"
          value={certificatePassword}
          onChange={(e) => setCertificatePassword(e.target.value)}
          maxLength={50}
          hideCounter
        />
      </div>

      {/*
        Pie homologado con ProductForm (/products), sin `sticky`: acá el
        contenido puede ser más corto que el modal, y un pie sticky se
        quedaba pegado al fondo dejando un hueco vacío antes de llegar a él.
      */}
      <div className="-mx-9 -mb-7 mt-7 flex flex-wrap items-center justify-between gap-3 border-t border-[#f0f0f0] bg-[#fafafa] px-9 py-[18px] dark:border-secondary-700 dark:bg-secondary-900/95">
        <Button data-testid="company-wizard-step3-back-button" variant="outline" onClick={onBack} disabled={isSubmitting} className={GHOST}>
          Anterior
        </Button>
        <Button data-testid="company-wizard-finish-button" variant="primary" onClick={handleFinish} isLoading={isSubmitting} className={CTA}>
          Terminar
        </Button>
      </div>
    </div>
  );
}
